/**
 * Runtime environment — cross-target.
 *
 * Web (Next.js): NEXT_PUBLIC_* via process.env (build-time)
 * Client (Vite): import.meta.env.VITE_* (build-time)
 * Tauri: same as Client + Tauri APIs available via `@tauri-apps/api`
 *
 * NB: runtime config (per Docker single-image strategy) si carica da
 * `/runtime-config.js` injected at container boot — pattern from v1.x preserved.
 */

export type RuntimeEnv = {
  /** BFF API base (e.g. `http://localhost:3000/api/v1`). All `/auth/*` calls go here. */
  apiUrl: string;
  /** WebSocket base. */
  wsUrl: string;
  /** Public BFF origin (e.g. `http://localhost:3000`). Used to redirect after logout. */
  webUrl: string;
  /**
   * Authenticated SPA origin (e.g. ``https://app.ando-ai.com``). Post-login
   * redirect target — distinct from ``webUrl`` when the BFF and the SPA sit
   * on different subdomains in prod (``beta.ando-ai.com`` vs
   * ``beta-web.ando-ai.com``). Falls back to ``webUrl`` for single-host dev.
   */
  appUrl?: string;
  /**
   * Cookie domain. Empty -> host-only cookie (local dev). In production this
   * MUST match the backend COOKIE_DOMAIN (e.g. `.ando-ai.com`) so the
   * `user_info` cookie is shared between BFF + SPA + API origins.
   */
  cookieDomain: string;
  appEnv: "local" | "staging" | "production";
  isTauri: boolean;
  /**
   * Zitadel issuer used by the **desktop** OAuth flow (RFC 8252 + PKCE).
   * Web build leaves this undefined — auth there rides on the BFF
   * cookie path. Tauri shells MUST set this to the same value as
   * ``ZITADEL_ISSUER`` on the BE so JWTs verify on every Bearer
   * request.
   */
  zitadelAuthority?: string;
  /**
   * Native OAuth client_id for the desktop flow. Provision a separate
   * "Native" application in the Zitadel console (project root →
   * Applications → New → Native) with redirect URI ``ando://callback``.
   * Local dev can fall back to the web ``ZITADEL_CLIENT_ID`` for
   * smoke testing.
   */
  zitadelDesktopClientId?: string;
  /**
   * Meta App ID consumed by the WhatsApp Embedded Signup popup
   * (``FB.login()`` via the Meta JS SDK). Get it from
   * https://developers.facebook.com/apps → your app → App settings →
   * Basic. Required for the WhatsApp Business connector; empty disables
   * the Embedded Signup CTA with a config-missing error.
   */
  metaAppId?: string;
  /**
   * Embedded Signup ``config_id`` — provisioned in Meta WhatsApp Manager
   * after Tech Provider verification. The config template MUST have
   * "Coexistence mode" enabled, otherwise Meta silently downgrades the
   * onboarding to classic Cloud API (mobile WhatsApp Business app gets
   * deregistered). The BE detection surfaces a UI warning on downgrade.
   */
  metaWhatsAppConfigId?: string;
  /**
   * Feature flag — mount the ONLYOFFICE Document Server editor
   * (``@ando/ui::OnlyOfficeEditor``) instead of the legacy inline
   * editors for docx/xlsx/pptx. Runtime-injected for prod (same image,
   * no rebuild) with a ``VITE_ENABLE_ONLYOFFICE`` fallback for local dev.
   * Defaults ``false`` — the BE ``/onlyoffice/config`` ``enabled`` field
   * is the authoritative second gate, so a stray flag never forces the
   * editor on without server support.
   */
  enableOnlyOffice: boolean;
};

declare global {
  interface Window {
    __ANDO_RUNTIME__?: Partial<RuntimeEnv>;
  }
}

let cached: RuntimeEnv | null = null;

export function getEnv(): RuntimeEnv {
  if (cached) return cached;

  const runtime = typeof window !== "undefined" ? window.__ANDO_RUNTIME__ : undefined;
  const isTauri = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

  // Default API URL — for web builds we want the relative ``/api/v1``
  // path (the docker frontend container rewrites it to the backend via
  // its proxy). For Tauri builds there's no proxy in front of the
  // WebView, so a relative URL would resolve against
  // ``tauri://localhost`` and 404 — every request needs the absolute
  // backend host. Pick the Vite-injected ``VITE_API_URL`` first (set
  // by ``docker-compose.yml::ando-client-v2`` and by the desktop
  // bundler), then fall back to the local-dev backend if neither the
  // runtime config nor the env var are present.
  //
  // ``import.meta.env`` typing varies across packages (``@ando/core``
  // ships without ``vite/client`` types because its consumers include
  // Node-side test runners). Access through an unknown-cast keeps the
  // module portable while still picking the value up at runtime.
  const importMeta = (
    typeof import.meta !== "undefined" ? (import.meta as unknown) : undefined
  ) as { env?: Record<string, string | undefined> } | undefined;
  const viteApiUrl = importMeta?.env?.VITE_API_URL ?? "";
  const defaultApiUrl = isTauri
    ? viteApiUrl || "http://localhost:8100/api/v1"
    : viteApiUrl || "/api/v1";

  // WhatsApp Embedded Signup keys — runtime-injected for prod (so the
  // same image runs across envs without rebuild) with a Vite-env
  // fallback for local dev (developer drops them in ``apps/client/.env``
  // and they're picked up immediately by Vite HMR — no container rebuild).
  //
  // Defense in depth: ``runtime-config.js`` templates can render
  // whitespace-only strings (e.g. ``envsubst`` of an unset env), and
  // those wouldn't be caught by plain ``||`` (an empty string IS falsy,
  // but a stringified-empty-then-padded value like ``" "`` isn't, and
  // we'd cache it as a "set" value). Trim + length-check to be safe.
  const pickConfig = (
    fromRuntime: string | undefined,
    fromVite: string | undefined,
  ): string | undefined => {
    const r = fromRuntime?.trim();
    if (r && r.length > 0) return r;
    const v = fromVite?.trim();
    if (v && v.length > 0) return v;
    return undefined;
  };
  const viteMetaAppId = importMeta?.env?.VITE_META_APP_ID;
  const viteWaConfigId = importMeta?.env?.VITE_WA_EMBEDDED_SIGNUP_CONFIG_ID;

  // Boolean feature flag — runtime config wins (already a boolean), else
  // parse the Vite-env string ("true"/"1" → true). Same defense-in-depth
  // trim as ``pickConfig`` so a whitespace-only envsubst value reads false.
  const pickFlag = (
    fromRuntime: boolean | string | undefined,
    fromVite: string | undefined,
  ): boolean => {
    // Runtime may deliver a real boolean (legacy) OR a string — the
    // ``runtime-config.template.js`` is rendered by ``envsubst``, which can
    // only emit strings ("true"/"false"). Coerce both before the Vite fallback.
    if (typeof fromRuntime === "boolean") return fromRuntime;
    const r = typeof fromRuntime === "string" ? fromRuntime.trim().toLowerCase() : "";
    if (r === "true" || r === "1") return true;
    if (r === "false" || r === "0") return false;
    const v = fromVite?.trim().toLowerCase();
    return v === "true" || v === "1";
  };
  const viteEnableOnlyOffice = importMeta?.env?.VITE_ENABLE_ONLYOFFICE;

  cached = {
    apiUrl: runtime?.apiUrl ?? defaultApiUrl,
    wsUrl: runtime?.wsUrl ?? "",
    webUrl: runtime?.webUrl ?? "",
    appUrl: runtime?.appUrl,
    cookieDomain: runtime?.cookieDomain ?? "",
    appEnv: (runtime?.appEnv ?? "local") as RuntimeEnv["appEnv"],
    isTauri,
    zitadelAuthority: runtime?.zitadelAuthority,
    zitadelDesktopClientId: runtime?.zitadelDesktopClientId,
    metaAppId: pickConfig(runtime?.metaAppId, viteMetaAppId),
    metaWhatsAppConfigId: pickConfig(runtime?.metaWhatsAppConfigId, viteWaConfigId),
    enableOnlyOffice: pickFlag(runtime?.enableOnlyOffice, viteEnableOnlyOffice),
  };

  return cached;
}
